import { ReactNode, useEffect } from 'react';

import {
    GetPathOptions,
    permissionsCompare,
    TertiaryLayoutSkeleton,
    useNavigation,
    useUserSession,
} from '@speeki/client-admin';

import { Permissions } from '@@types/CommonTypes';

import { authService } from '@services/AuthService/AuthService';

interface PermissionRouteProps {
    atLeastOne?: boolean;
    children: ReactNode;
    /**
     * Additional condition if {@link permissions} are insufficient.
     */
    condition?: boolean;
    /**
     * Event fired before {@link redirectTo}.
     */
    onRedirect?: () => void;
    permissions: Permissions[];
    /**
     * The path to which the user will be redirected when {@link condition} or {@link permissions} are not met.
     */
    redirectTo: GetPathOptions;
}

/**
 * IMPORTANT! PermissionRoute requires user data before render.
 */
export const PermissionRoute = ({
    atLeastOne,
    children,
    condition = true,
    onRedirect,
    permissions,
    redirectTo,
}: PermissionRouteProps) => {
    const { navigate } = useNavigation();

    const { data: { user } = {}, loading: userLoading } = useUserSession();

    const userPermissions = [
        ...(user?.permissions ?? []),
        ...(user?.tenant?.features ?? []),
    ];

    const logOutCondition = !user;
    const validCondition =
        !!condition &&
        permissionsCompare(permissions, userPermissions, atLeastOne);

    const showSkeleton = userLoading || logOutCondition || !validCondition;

    useEffect(() => {
        if (!userLoading) {
            if (logOutCondition)
                authService.logout({ logoutWithRedirectLink: true });
            else if (!validCondition) {
                onRedirect?.();
                navigate(redirectTo);
            }
        }
    }, [logOutCondition, redirectTo, userLoading, validCondition]);

    return <>{showSkeleton ? <TertiaryLayoutSkeleton /> : children}</>;
};
